import { memo } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { FormattedMessage, FormattedPlural } from "react-intl";
import { FLIGHT_OPTIONS_STATUS, TRANSPORTATION_TYPES } from "app/constants";
import "./LuggageSummary.scss";
import LuggagePlaceholderLoader from "app/pages/Booking/Quotation/Luggage/LuggagePlaceholderLoader/LuggagePlaceholderLoader";
import classNames from "classnames";
import IconHandLuggageAvailable from "app/pages/.shared/IconHandLuggageAvailable";
import IconCarryOnLuggageAvailable from "app/pages/.shared/static/icons/IconCarryOnLuggageAvailable";
import IconCheckInLuggageUnavailable from "app/pages/.shared/static/icons/IconCheckInLuggageUnavailable";
import IconCheckInLuggageAvailable from "app/pages/.shared/static/icons/IconCheckInLuggageAvailable";
import IconHandLuggageUnavailable from "app/pages/.shared/IconHandLuggageUnavailable";
import IconCarryOnLuggageUnavailable from "app/pages/.shared/static/icons/IconCarryOnLuggageUnavailable";

const LuggageSummary = ({
	flight = {},
	flightOptions = {},
	bookingLuggageCount,
	luggageMaxCount,
	transportationType,
	onLuggageClick = () => {},
	hideAddLuggageButton,
	flightOptionsStatus,
	maxWeightPerCheckedBaggage = 0,
}) => {
	const isTravelByTrain = transportationType === TRANSPORTATION_TYPES.TRAIN;
	const luggage = flightOptions?.flightBagageOption || flight?.flightOptions?.flightBagageOption;
	const totalLuggageCount = get(luggage, "quantity");

	let luggageCount = 0;

	if (bookingLuggageCount) {
		luggageCount = bookingLuggageCount;
	} else if (totalLuggageCount) {
		luggageCount = totalLuggageCount;
	} else if (get(luggage, "included")) {
		const luggageMaxCountPerPassenger = Math.floor(totalLuggageCount / luggageMaxCount);
		luggageCount = totalLuggageCount ? luggageMaxCountPerPassenger : 1;
	} else if (get(luggage, "canBeBooked")) {
		luggageCount = 0;
	}

	const isCabinLuggageNotIncluded = get(luggage, "cabinBaggage.included") === false;

	const isBaggageNotBookable =
		get(luggage, "included") === false && get(luggage, "canBeBooked") === false;

	const isBaggageBookable = luggage?.canBeBooked && !luggage?.included;

	const isLoading = flightOptionsStatus === FLIGHT_OPTIONS_STATUS.LOADING;

	if (isLoading) {
		return (
			<div className="luggage-summary__placeholder-loader">
				<LuggagePlaceholderLoader />
			</div>
		);
	}

	let luggageDisplayValue = "";

	let handLuggageDetails = luggage?.handBaggage?.details;
	let cabinLuggageDetails = luggage?.cabinBaggage?.details;

	if (isBaggageNotBookable) {
		luggageDisplayValue = (
			<>
				: <FormattedMessage id="quotation.luggage.not.available" />
			</>
		);
	} else if (!hideAddLuggageButton && isBaggageBookable) {
		luggageDisplayValue = (
			<>
				{": "}
				<span
					className="luggage-summary__add"
					onClick={onLuggageClick}
					data-testid="luggage-summary-add"
				>
					+{" "}
					<span>
						<FormattedMessage id="quotation.luggage.bookable.label" />
					</span>
				</span>
			</>
		);
	}

	const getLuggageClassNames = (count, maxCount, isIncluded) => {
		return {
			itemClassName: classNames("luggage-summary--item", {
				"luggage-summary--item-not-included": count === 0 || isIncluded === false,
			}),
			labelClassName: classNames("luggage-summary__label", {
				"luggage-summary__label--included": isIncluded,
			}),
			countClassName: classNames("luggage-summary__count", {
				"luggage-summary__count--included": count > 0 || maxCount > 0,
			}),
		};
	};

	const handLuggageClassNames = getLuggageClassNames(
		luggageMaxCount,
		luggageMaxCount,
		luggageMaxCount > 0
	);
	const cabinLuggageClassNames = getLuggageClassNames(
		luggageMaxCount,
		luggageMaxCount,
		!isCabinLuggageNotIncluded
	);
	const holdLuggageClassNames = getLuggageClassNames(
		luggageCount,
		luggageMaxCount,
		luggageCount > 0
	);

	return (
		<div className="luggage-summary">
			{/* BAGAGE A MAIN */}
			{isCabinLuggageNotIncluded && (
				<div className={handLuggageClassNames.itemClassName}>
					<span className="luggage-summary__icon">
						{luggageMaxCount > 0 ? (
							<IconHandLuggageAvailable />
						) : (
							<IconHandLuggageUnavailable />
						)}
					</span>
					<span
						className={handLuggageClassNames.labelClassName}
						data-testid="luggage-summary-label"
					>
						{luggageMaxCount >= 0 && (
							<span
								className={handLuggageClassNames.countClassName}
								data-testid="hand-luggage-summary-count"
							>
								{luggageMaxCount}x
							</span>
						)}
						<FormattedMessage id="quotation.luggage.handbag.plural.label" />
						{handLuggageDetails && <> ({handLuggageDetails})</>}
					</span>
				</div>
			)}

			{/* BAGAGE EN CABINE */}
			<div className={cabinLuggageClassNames.itemClassName}>
				<span className="luggage-summary__icon">
					{isCabinLuggageNotIncluded ? (
						<IconCarryOnLuggageUnavailable />
					) : (
						<IconCarryOnLuggageAvailable />
					)}
				</span>
				<span
					className={cabinLuggageClassNames.labelClassName}
					data-testid="luggage-summary-cabin-label"
				>
					{luggageMaxCount >= 0 && (
						<span
							className={cabinLuggageClassNames.countClassName}
							data-testid="cabin-luggage-summary-count"
						>
							{isCabinLuggageNotIncluded ? 0 : luggageMaxCount}x
						</span>
					)}
					{isTravelByTrain ? (
						<FormattedMessage id="quotation.transport.luggage.small.plural" />
					) : (
						<FormattedPlural
							value={isCabinLuggageNotIncluded ? 0 : luggageMaxCount}
							one={<FormattedMessage id="quotation.luggage.cabin.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.cabin.plural.label" />}
						/>
					)}{" "}
					{isCabinLuggageNotIncluded ? (
						<>
							: <FormattedMessage id="quotation.luggage.cabin.description.message" />
						</>
					) : (
						<>{cabinLuggageDetails && <> ({cabinLuggageDetails})</>}</>
					)}
				</span>
			</div>
			{/* BAGAGE EN SOUTE */}
			<div className={holdLuggageClassNames.itemClassName}>
				<span className="luggage-summary__icon">
					{luggageCount > 0 ? (
						<IconCheckInLuggageAvailable />
					) : (
						<IconCheckInLuggageUnavailable />
					)}
				</span>
				<span
					className={holdLuggageClassNames.labelClassName}
					data-testid="luggage-summary-label"
				>
					<span
						className={holdLuggageClassNames.countClassName}
						data-testid="hold-luggage-summary-count"
					>
						{luggageCount}x
					</span>
					{isTravelByTrain ? (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.transport.luggage.regular" />}
							other={
								<FormattedMessage id="quotation.transport.luggage.regular.pluralized" />
							}
						/>
					) : (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.luggage.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.plural.label" />}
						/>
					)}{" "}
					{maxWeightPerCheckedBaggage > 0 && (
						<span data-testid="max-weight-per-checked-baggage">
							(
							<FormattedMessage
								id="quotation.transport.luggage.option.max.weight"
								values={{
									weight: maxWeightPerCheckedBaggage,
								}}
							/>
							)
						</span>
					)}
					{luggageDisplayValue}
				</span>
			</div>
		</div>
	);
};

LuggageSummary.propTypes = {
	transportationType: PropTypes.oneOf([TRANSPORTATION_TYPES.FLIGHT, TRANSPORTATION_TYPES.TRAIN]),
	onLuggageClick: PropTypes.func,
	flight: PropTypes.object,
	bookingLuggageCount: PropTypes.number,
	luggageMaxCount: PropTypes.number,
	hideAddLuggageButton: PropTypes.bool,
	flightOptions: PropTypes.object,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
	maxWeightPerCheckedBaggage: PropTypes.number,
};

export default memo(LuggageSummary);
